import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Plus,
  Trash,
  PencilSimple,
  User,
  Airplane,
  Bandaids,
  Prohibit,
  MagnifyingGlass,
  CaretDown,
} from "@phosphor-icons/react";
import { toISO, fromISO, formatFull } from "@/lib/dates";
import AbsenceHeatmap from "@/components/AbsenceHeatmap";
import StaffYearGrid from "@/components/StaffYearGrid";

const TYPE_META = {
  holiday: {
    label: "HOLIDAY",
    icon: Airplane,
    bg: "bg-amber-50",
    border: "border-amber-500",
    text: "text-amber-800",
    solid: "bg-amber-400 text-amber-950",
  },
  sickness: {
    label: "SICKNESS",
    icon: Bandaids,
    bg: "bg-rose-50",
    border: "border-rose-500",
    text: "text-rose-800",
    solid: "bg-rose-500 text-white",
  },
  absence: {
    label: "ABSENCE",
    icon: Prohibit,
    bg: "bg-zinc-100",
    border: "border-zinc-600",
    text: "text-zinc-800",
    solid: "bg-zinc-700 text-white",
  },
};

function daysBetween(start, end) {
  const a = fromISO(start).getTime();
  const b = fromISO(end).getTime();
  return Math.floor((b - a) / 86400000) + 1;
}

function todayISO() {
  return toISO(new Date());
}

function defaultStart() {
  return "2026-04-01";
}

function statusOf(a, today) {
  if (a.end_date < today) return "past";
  if (a.start_date > today) return "upcoming";
  return "current";
}

export default function RotaPage() {
  const [staff, setStaff] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffFilter, setStaffFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState(null); // iso date to filter table
  const [heatmapMonth, setHeatmapMonth] = useState(new Date(2026, 3, 1));
  const [viewMode, setViewMode] = useState("year"); // "year" | "month"
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    staff_id: "",
    start_date: defaultStart(),
    end_date: defaultStart(),
    type: "holiday",
    note: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.listStaff(), api.listAbsences()]);
      setStaff(s);
      setAbsences(a);
    } catch {
      toast.error("Failed to load rota");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const staffById = useMemo(() => {
    const m = {};
    for (const s of staff) m[s.id] = s;
    return m;
  }, [staff]);

  // Today for status coloring — use a stable "reference today" mid-schedule so demo data has current entries too
  const today = todayISO();

  const filtered = useMemo(() => {
    let list = absences;
    if (typeFilter !== "ALL") list = list.filter((a) => a.type === typeFilter.toLowerCase());
    if (statusFilter !== "ALL") {
      list = list.filter((a) => statusOf(a, today) === statusFilter.toLowerCase());
    }
    if (dateFilter) {
      list = list.filter((a) => a.start_date <= dateFilter && a.end_date >= dateFilter);
    }
    if (staffFilter.trim()) {
      const q = staffFilter.trim().toLowerCase();
      list = list.filter((a) => {
        const name = staffById[a.staff_id]?.name?.toLowerCase() || "";
        return name.includes(q);
      });
    }
    return list;
  }, [absences, typeFilter, statusFilter, dateFilter, staffFilter, staffById, today]);

  const counts = useMemo(() => {
    const c = { holiday: 0, sickness: 0, absence: 0, days: 0 };
    for (const a of absences) {
      c[a.type] = (c[a.type] || 0) + 1;
      c.days += daysBetween(a.start_date, a.end_date);
    }
    return c;
  }, [absences]);

  const openNew = () => {
    setEditing(null);
    setForm({
      staff_id: "",
      start_date: defaultStart(),
      end_date: defaultStart(),
      type: "holiday",
      note: "",
    });
    setDialogOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      staff_id: a.staff_id,
      start_date: a.start_date,
      end_date: a.end_date,
      type: a.type,
      note: a.note || "",
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.staff_id) {
      toast.error("Pick a staff member");
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    try {
      if (editing) await api.updateAbsence(editing.id, form);
      else await api.createAbsence(form);
      setDialogOpen(false);
      await load();
      toast.success(editing ? "Rota updated" : "Absence logged");
    } catch {
      toast.error("Save failed");
    }
  };
  const del = async (a) => {
    if (!window.confirm("Remove this rota entry?")) return;
    try {
      await api.deleteAbsence(a.id);
      await load();
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const selectedStaff = staffById[form.staff_id];

  return (
    <div className="p-6 min-h-screen">
      <header className="flex items-end justify-between border-b-2 border-black pb-4 mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight leading-none">STAFF ROTA</h1>
          <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mt-2">
            HOLIDAY · SICKNESS · ABSENCE — {absences.length} ENTRIES · {counts.days} TOTAL DAYS
          </div>
        </div>
        <Button
          onClick={openNew}
          data-testid="add-absence-btn"
          className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 font-mono-plex text-xs tracking-widest active:scale-[0.98]"
        >
          <Plus size={14} weight="bold" className="mr-2" /> LOG ABSENCE
        </Button>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <TypeCard t="holiday" count={counts.holiday} />
        <TypeCard t="sickness" count={counts.sickness} />
        <TypeCard t="absence" count={counts.absence} />
      </div>

      {/* View toggle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex border border-black" data-testid="rota-view-toggle">
          {[
            { key: "year", label: "YEAR VIEW" },
            { key: "month", label: "MONTH HEATMAP" },
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key)}
              data-testid={`rota-view-${v.key}`}
              className={`px-4 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors ${
                viewMode === v.key ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Primary view: Year grid OR Month heatmap */}
      {viewMode === "year" ? (
        <div className="mb-6">
          <StaffYearGrid
            staff={staff}
            absences={absences}
            startDate="2026-03-31"
            endDate="2027-03-30"
            onCellClick={(s, iso, existingType) => {
              setEditing(null);
              setForm({
                staff_id: s.id,
                start_date: iso,
                end_date: iso,
                type: existingType || "holiday",
                note: "",
              });
              setDialogOpen(true);
            }}
          />
        </div>
      ) : (
        <div className="mb-6">
          <AbsenceHeatmap
            monthDate={heatmapMonth}
            onMonthChange={setHeatmapMonth}
            absences={absences}
            selectedDate={dateFilter}
            onSelectDate={setDateFilter}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={staffFilter}
            onChange={(e) => setStaffFilter(e.target.value)}
            placeholder="Search staff…"
            data-testid="rota-search"
            className="pl-8 h-9 w-56 border-black rounded-none font-mono-plex text-xs"
          />
        </div>
        <div className="flex border border-black" data-testid="rota-type-filter">
          {["ALL", "HOLIDAY", "SICKNESS", "ABSENCE"].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              data-testid={`rota-type-${t}`}
              className={`px-3 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors ${
                typeFilter === t ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex border border-black" data-testid="rota-status-filter">
          {["ALL", "CURRENT", "UPCOMING", "PAST"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`rota-status-${s}`}
              className={`px-3 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors ${
                statusFilter === s ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border-2 border-black bg-white" data-testid="rota-table">
        {dateFilter && (
          <div className="px-4 py-2 border-b-2 border-black bg-amber-50 flex items-center justify-between">
            <div className="font-mono-plex text-[10px] tracking-widest text-amber-900">
              FILTERED TO <span className="font-bold">{dateFilter}</span> — {filtered.length} MATCHING
            </div>
            <button
              onClick={() => setDateFilter(null)}
              className="text-[10px] font-mono-plex tracking-widest underline hover:no-underline"
              data-testid="clear-date-filter-inline"
            >
              CLEAR
            </button>
          </div>
        )}
        <div className="grid grid-cols-[1fr_140px_170px_170px_1fr_120px] font-heading font-bold text-[10px] tracking-widest border-b-2 border-black bg-zinc-50">
          <div className="px-4 py-3">STAFF</div>
          <div className="px-4 py-3 border-l border-zinc-300">TYPE</div>
          <div className="px-4 py-3 border-l border-zinc-300">START</div>
          <div className="px-4 py-3 border-l border-zinc-300">END</div>
          <div className="px-4 py-3 border-l border-zinc-300">NOTE</div>
          <div className="px-4 py-3 border-l border-zinc-300 text-right">ACTIONS</div>
        </div>
        {loading && (
          <div className="p-6 font-mono-plex text-xs text-zinc-500">LOADING…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center font-mono-plex text-xs text-zinc-500">
            NO ROTA ENTRIES MATCH — TAP <span className="font-semibold">LOG ABSENCE</span> TO ADD ONE
          </div>
        )}
        {filtered.map((a) => {
          const meta = TYPE_META[a.type];
          const Icon = meta.icon;
          const s = staffById[a.staff_id];
          const days = daysBetween(a.start_date, a.end_date);
          const status = statusOf(a, today);
          return (
            <div
              key={a.id}
              className="grid grid-cols-[1fr_140px_170px_170px_1fr_120px] border-b border-zinc-200 hover:bg-zinc-50"
              data-testid={`absence-row-${a.id}`}
            >
              <div className="px-4 py-3 flex items-center gap-2 font-mono-plex text-sm">
                <User size={14} className="text-zinc-500" />
                <span className="font-medium">{s?.name || "—"}</span>
                <span className="text-[9px] uppercase tracking-widest text-zinc-500 ml-1">
                  {s?.role}
                </span>
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 font-mono-plex text-[10px] tracking-widest flex items-center">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${meta.solid}`}>
                  <Icon size={10} weight="bold" />
                  {meta.label}
                </span>
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 font-mono-plex text-xs">
                {a.start_date}
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 font-mono-plex text-xs">
                {a.end_date}
                <div className="text-[9px] text-zinc-500 tracking-widest mt-0.5">
                  {days} DAY{days === 1 ? "" : "S"} · {status.toUpperCase()}
                </div>
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 font-mono-plex text-xs text-zinc-700">
                {a.note || <span className="text-zinc-400">—</span>}
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => openEdit(a)}
                  data-testid={`edit-absence-${a.id}`}
                  className="p-1.5 border border-black hover:bg-black hover:text-white transition-colors"
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
                <button
                  onClick={() => del(a)}
                  data-testid={`delete-absence-${a.id}`}
                  className="p-1.5 border border-black hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#0A0A0A] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              {editing ? "EDIT ROTA ENTRY" : "LOG ABSENCE"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">STAFF</label>
              <StaffPicker
                staff={staff}
                value={form.staff_id}
                onChange={(id) => setForm({ ...form, staff_id: id })}
                selectedName={selectedStaff?.name}
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">TYPE</label>
              <div className="grid grid-cols-3 gap-0 border border-black">
                {Object.entries(TYPE_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  const active = form.type === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setForm({ ...form, type: key })}
                      data-testid={`absence-type-${key}`}
                      className={`flex items-center justify-center gap-1.5 py-2 text-[10px] font-mono-plex tracking-widest transition-colors border-r border-black last:border-r-0 ${
                        active ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
                      }`}
                    >
                      <Icon size={12} weight="bold" />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">START</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="absence-form-start"
                  className="rounded-none border-black font-mono-plex text-xs"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">END</label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="absence-form-end"
                  className="rounded-none border-black font-mono-plex text-xs"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">
                NOTE <span className="text-zinc-400 normal-case tracking-normal">(optional)</span>
              </label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. GP appointment, family holiday…"
                data-testid="absence-form-note"
                className="rounded-none border-black font-mono-plex text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100"
            >
              CANCEL
            </button>
            <button
              onClick={save}
              data-testid="absence-form-save"
              className="px-4 py-2 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98]"
            >
              SAVE
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TypeCard({ t, count }) {
  const meta = TYPE_META[t];
  const Icon = meta.icon;
  return (
    <div
      className={`border-2 border-black bg-white p-4 flex items-center justify-between ${meta.bg}`}
      data-testid={`rota-kpi-${t}`}
    >
      <div>
        <div className="text-[10px] font-mono-plex tracking-widest text-zinc-600">
          {meta.label}
        </div>
        <div className="font-heading font-black text-3xl tracking-tight leading-none mt-2">
          {count}
        </div>
      </div>
      <div className={`p-3 ${meta.solid}`}>
        <Icon size={22} weight="bold" />
      </div>
    </div>
  );
}

function StaffPicker({ staff, value, onChange, selectedName }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="absence-form-staff"
          className="w-full flex items-center justify-between border border-black px-3 py-2 text-xs font-mono-plex hover:bg-zinc-100 transition-colors"
        >
          <span className={value ? "" : "text-zinc-500"}>
            {selectedName || "Pick a staff member…"}
          </span>
          <CaretDown size={12} weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0 border-2 border-black rounded-none shadow-[4px_4px_0_0_#0A0A0A]"
      >
        <Command>
          <CommandInput placeholder="Search staff…" className="font-mono-plex text-xs" />
          <CommandList className="max-h-64">
            <CommandEmpty>No staff.</CommandEmpty>
            <CommandGroup>
              {staff.map((s) => (
                <CommandItem
                  key={s.id}
                  value={s.name}
                  onSelect={() => {
                    onChange(s.id);
                    setOpen(false);
                  }}
                  className="font-mono-plex text-xs"
                >
                  <User size={12} className="mr-2 text-zinc-500" />
                  <span className="flex-1">{s.name}</span>
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">
                    {s.role}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
